// Wraps deal + solver: produce a deal that the thoughtful solver verifies as solvable.
// Solver runs in a Web Worker so the UI thread is never blocked.

import type { Card, DrawMode, GameState } from "./types.ts";
import { dealKlondike } from "./deal.ts";
import { randomSeed } from "./rng.ts";
import { RULES } from "../config/constants.ts";
import SolverWorker from "./solver.worker.ts?worker";
import type { SolveRequest, SolveResponse } from "./solver.worker.ts";
import {
  cacheNeedsRefill,
  popCachedSeed,
  pushSeed,
  rememberPlayedSeed,
  refillCache,
} from "../sync/deals-pool.ts";

let workerInstance: Worker | null = null;
let nextReqId = 1;

function getWorker(): Worker {
  if (workerInstance) return workerInstance;
  workerInstance = new SolverWorker();
  return workerInstance;
}

function solveInWorker(
  req: Omit<SolveRequest, "id">,
  timeoutMs: number,
): Promise<SolveResponse | null> {
  return new Promise((resolve) => {
    const id = nextReqId++;
    const w = getWorker();
    const onMessage = (e: MessageEvent<SolveResponse>) => {
      if (e.data.id !== id) return;
      w.removeEventListener("message", onMessage);
      window.clearTimeout(timer);
      resolve(e.data);
    };
    w.addEventListener("message", onMessage);
    const timer = window.setTimeout(() => {
      w.removeEventListener("message", onMessage);
      w.terminate();
      if (workerInstance === w) workerInstance = null;
      resolve(null);
    }, timeoutMs);
    w.postMessage({ ...req, id } as SolveRequest);
  });
}

export interface GenerateOptions {
  attempts?: number;
  perAttemptTimeoutMs?: number;
  onProgress?: (info: { attempt: number; total: number | null }) => void;
}

export async function generateSolvableGame(
  drawMode: DrawMode,
  opts: GenerateOptions = {},
): Promise<GameState> {
  const perTimeout = opts.perAttemptTimeoutMs ?? 2500;
  const maxStates =
    drawMode === "one"
      ? RULES.SOLVER_MAX_STATES_ONE
      : RULES.SOLVER_MAX_STATES_THREE;
  const maxDepth =
    drawMode === "one"
      ? RULES.SOLVER_MAX_DEPTH_ONE
      : RULES.SOLVER_MAX_DEPTH_THREE;

  // 1. Try the local cache (already-fetched server seeds).
  const cachedGame = await popVerifiedCachedGame(drawMode, maxStates, maxDepth, perTimeout);
  if (cachedGame) {
    void maybeRefillInBackground(drawMode);
    return prepareGame(cachedGame);
  }

  // 2. Try a quick server fetch (small timeout — don't block too long).
  try {
    const fetched = await Promise.race([
      refillCache(drawMode),
      new Promise<number>((resolve) => setTimeout(() => resolve(0), 1500)),
    ]);
    if (fetched > 0) {
      const game = await popVerifiedCachedGame(drawMode, maxStates, maxDepth, perTimeout);
      if (game) {
        void maybeRefillInBackground(drawMode);
        return prepareGame(game);
      }
    }
  } catch {
    // Network error — fall through to local generation.
  }

  // 3. Local generation (offline, empty pool, or slow server).
  const game = await generateLocally(drawMode, opts);
  rememberPlayedSeed(drawMode, game.seed);
  // Best-effort: feed the pool so the next player benefits.
  void pushSeed(game.seed, drawMode);
  return game;
}

async function popVerifiedCachedGame(
  drawMode: DrawMode,
  maxStates: number,
  maxDepth: number,
  timeoutMs: number,
): Promise<GameState | null> {
  for (let i = 0; i < 8; i++) {
    const seed = popCachedSeed(drawMode);
    if (!seed) return null;
    const game = dealKlondike(seed, drawMode);
    const res = await solveInWorker(
      {
        tableau: game.tableau,
        stock: game.stock,
        drawMode,
        maxStates,
        maxDepth,
      },
      timeoutMs,
    );
    if (res?.solvable) return game;
    console.warn("[generate] skipped unverified pool seed:", seed);
  }
  return null;
}

function prepareGame(game: GameState): GameState {
  rememberPlayedSeed(game.drawMode, game.seed);
  return game;
}

function maybeRefillInBackground(drawMode: DrawMode): Promise<unknown> {
  if (!cacheNeedsRefill(drawMode)) return Promise.resolve();
  return refillCache(drawMode).catch(() => 0);
}

async function generateLocally(
  drawMode: DrawMode,
  opts: GenerateOptions,
): Promise<GameState> {
  const attempts = opts.attempts ?? Infinity;
  const perTimeout = opts.perAttemptTimeoutMs ?? 2500;
  const maxStates =
    drawMode === "one"
      ? RULES.SOLVER_MAX_STATES_ONE
      : RULES.SOLVER_MAX_STATES_THREE;
  const maxDepth =
    drawMode === "one"
      ? RULES.SOLVER_MAX_DEPTH_ONE
      : RULES.SOLVER_MAX_DEPTH_THREE;

  for (let i = 0; i < attempts; i++) {
    opts.onProgress?.({
      attempt: i + 1,
      total: Number.isFinite(attempts) ? attempts : null,
    });
    const seed = randomSeed();
    const game = dealKlondike(seed, drawMode);
    const res = await solveInWorker(
      {
        tableau: game.tableau,
        stock: game.stock,
        drawMode,
        maxStates,
        maxDepth,
      },
      perTimeout,
    );
    if (res?.solvable) return game;
  }
  throw new Error("Could not generate a verified solvable deal.");
}

// Check if the game is still winnable from its current mid-game state.
// Uses the same Web Worker so the UI thread stays responsive.
// Returns null if the worker timed out (treat as "unknown").
export async function checkCurrentSolvable(
  state: GameState,
  timeoutMs = 1500,
): Promise<boolean | null> {
  const tops: { S: number; H: number; D: number; C: number } = {
    S: 0, H: 0, D: 0, C: 0,
  };
  for (const pile of state.foundation) {
    const top = pile[pile.length - 1];
    if (top) tops[top.suit] = top.rank;
  }
  // Stock cards are face-down in GameState; the solver flips them face-up
  // internally, but we pass shallow copies to avoid touching player state.
  const tableau: Card[][] = state.tableau.map((p) => p.map((c) => ({ ...c })));
  const stock: Card[] = state.stock.map((c) => ({ ...c }));
  const waste: Card[] = state.waste.map((c) => ({ ...c }));

  const res = await solveInWorker(
    {
      tableau,
      stock,
      drawMode: state.drawMode,
      maxStates: 80_000,
      maxDepth:
        state.drawMode === "one"
          ? RULES.SOLVER_MAX_DEPTH_ONE
          : RULES.SOLVER_MAX_DEPTH_THREE,
      foundationTops: tops,
      initialWaste: waste,
    },
    timeoutMs,
  );
  if (!res) return null;
  return res.solvable;
}
